import { useState } from "react";
import axios from "axios";
import { isValidPhoneNumber } from "react-phone-number-input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ENQUIRY_TYPES = [
  "General Enquiry",
  "Strategic Partnership",
  "Investment or Business Opportunity",
  "Monarch Estates Enquiry",
  "Monarch Design Studio Enquiry",
  "The Art Forest Enquiry",
  "The Wishing Well Enquiry",
  "Media or Speaking Enquiry",
  "Other",
];

// Pure validity/payload helpers, extracted so the hook body doesn't
// carry this branching/shaping logic inline -- mirrors the same
// pattern already used in AppointmentForm.js (buildAppointmentPayload,
// computeCanSubmit).
const computeEmailValid = (email) => EMAIL_PATTERN.test(email.trim());
const computePhoneValid = (phone, isValidPhoneNumber) => !phone || isValidPhoneNumber(phone);

const computeCanSubmit = ({ fullName, emailValid, phoneValid, enquiryType, consent, turnstileToken, submitting }) =>
  fullName.trim().length > 1 &&
  emailValid &&
  phoneValid &&
  !!enquiryType &&
  consent &&
  !!turnstileToken &&
  !submitting;

const buildContactPayload = (fields) => ({
  full_name: fields.fullName,
  email: fields.email,
  phone: fields.phone || null,
  phone_country: fields.phoneCountry,
  phone_dial_code: fields.phoneDialCode,
  company: fields.company || null,
  enquiry_type: fields.enquiryType,
  message: fields.message.trim() || null,
  consent: fields.consent,
  source_page: "/contact",
  website: fields.honeypot,
  turnstile_token: fields.turnstileToken,
  form_rendered_at: fields.formRenderedAt,
});

// Extracted network call so handleSubmit only orchestrates state, not
// request wiring -- also makes this call independently testable.
const submitContact = (payload) => axios.post(`${API}/contact/submit`, payload);

// Owns all Contact-form ("Send an Enquiry") field state and the submit
// request. Phone is optional here (required only on the separate
// Appointment-booking form) -- kept out of the page component so
// Contact.js only has to wire fields to inputs and render JSX.
export const useContactForm = (preselectedEnquiry) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(""); // E.164, optional
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneDialCode, setPhoneDialCode] = useState(null);
  const [company, setCompany] = useState("");
  const [enquiryType, setEnquiryType] = useState(
    ENQUIRY_TYPES.includes(preselectedEnquiry) ? preselectedEnquiry : ""
  );
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // anti-spam trap, kept empty by real visitors
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formRenderedAt] = useState(() => Date.now());

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handlePhoneChange = ({ value, country, dialCode }) => {
    setPhone(value || "");
    setPhoneCountry(country);
    setPhoneDialCode(dialCode);
  };

  const emailValid = computeEmailValid(email);
  const phoneValid = computePhoneValid(phone, isValidPhoneNumber);

  const canSubmit = computeCanSubmit({ fullName, emailValid, phoneValid, enquiryType, consent, turnstileToken, submitting });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = buildContactPayload({
        fullName, email, phone, phoneCountry, phoneDialCode, company,
        enquiryType, message, consent, honeypot, turnstileToken, formRenderedAt,
      });
      await submitContact(payload);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    fields: { fullName, email, phone, company, enquiryType, message, consent, honeypot, turnstileToken },
    setters: {
      setFullName,
      setEmail,
      setPhone: handlePhoneChange,
      setCompany,
      setEnquiryType,
      setMessage,
      setConsent,
      setHoneypot,
      setTurnstileToken,
    },
    validity: { emailValid, phoneValid },
    submission: { submitting, submitError, submitted, canSubmit, handleSubmit },
  };
};
